import type { Category } from '../types/GraphQL';
import { useBFF } from '~/composables/useBFF/useBFF';

export const categoryGetters = {
  getId: (category: Category) => category?.id,
  getName: (category: Category) => category?.name,
  getSlug: (category: Category) => category?.slug,
  getCanonical: (category: Category & { canonicalUrl?: string }) => category?.canonicalUrl,
};

export interface NavbarCategory {
  name: string;
  slug: string;
  id: string;
  key: string;
  slugAllLocales?: any;
  hideChartSize?: boolean;
  childrenItems?: NavbarCategory[] | null;
}

const flattenCategories = (childrenItems: NavbarCategory[]): Record<string, string> => (
  childrenItems?.reduce((acc: Record<string, string>, category) => {
    acc[category.slug] = category.key;
    if (category.childrenItems) {
      return { ...acc, ...flattenCategories(category.childrenItems) };
    }
    return acc;
  }, {})
);

export const useCategories = () => {
  const { $categories } = useBFF();

  const result = useState<NavbarCategory[]>('useCategories');
  const loading = ref(false);
  const rootCategory = useState<NavbarCategory>('useCategoriesRoot');

  const load = async () => {
    loading.value = true;
    try {
      const { data: categories } = await $categories.getCategories();

      if (!categories.childrenItems) {
        throw new Error('No categories found');
      }

      result.value = categories.childrenItems;
      rootCategory.value = {
        name: categories.name,
        slug: categories.slug,
        id: '',
        key: categories.key,
      };
    }
    finally {
      loading.value = false;
    }
  };

  return {
    result: computed(() => result.value),
    flatCategories: computed(() => flattenCategories(result.value)),
    loading: computed(() => loading.value),
    rootCategory: computed(() => rootCategory.value),

    load,
  };
};
